import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { BookOpen, CheckCircle2, Star, AlertTriangle } from "lucide-react";

export default function ParentCoursesPanel({ onChange, onPunchIn }) {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/parent/enrollments");
      setEnrollments(data || []);
    } catch { /* silent */ }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/parent/enrollments")
      .then(({ data }) => {
        if (!cancelled) setEnrollments(data || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handlePunchInClick = (enrollment) => {
    if (onPunchIn) onPunchIn(enrollment);
  };

  return (
    <div className="bg-white rounded-3xl p-6 border border-black/5" data-testid="parent-courses-panel">
      <h2 className="font-display text-xl font-bold uppercase inline-flex items-center gap-2" style={{ color: "#13A699" }}>
        <BookOpen className="h-5 w-5" /> My Courses
      </h2>
      {loading ? (
        <p className="text-sm mt-3" style={{ color: "#13A69988" }}>Loading…</p>
      ) : enrollments.length === 0 ? (
        <p className="text-sm mt-3" style={{ color: "#13A69988" }}>No course enrollments yet. Browse a teacher's profile to enroll.</p>
      ) : (
        <ul className="mt-3 space-y-2" data-testid="parent-enrollments">
          {enrollments.map((e) => (
            <li
              key={e.id}
              className="rounded-2xl border border-black/5 p-3 flex flex-wrap items-center justify-between gap-2"
              data-testid={`parent-enrollment-${e.id}`}
            >
              <div className="min-w-0">
                <div className="font-display font-bold" style={{ color: "#13A699" }}>{e.course_title}</div>
                <div className="text-xs" style={{ color: "#13A69988" }}>
                  with {e.teacher_name} • {e.num_sessions} sessions • ₹{e.fees}
                  {e.child_snapshot?.name ? ` • for ${e.child_snapshot.name}` : ""}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={e.status} />
                {e.status === "teacher_completed" && (
                  <button
                    onClick={() => handlePunchInClick(e)}
                    className="text-xs px-3 py-1.5 rounded-xl bg-[#13A699] text-white hover:opacity-90 inline-flex items-center gap-1"
                    data-testid={`confirm-complete-${e.id}`}
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Punch In &amp; Rate
                  </button>
                )}
                {e.rated && (
                  <span className="text-xs font-bold inline-flex items-center gap-1" style={{ color: "#13A699" }}>
                    <Star className="h-3 w-3 fill-current" /> {e.rating}
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Active", bg: "#FFD708", color: "#13A699" },
    teacher_completed: { label: "Mentor marked done", bg: "#FBA31222", color: "#FBA312" },
    completed: { label: "Completed", bg: "#13A69922", color: "#13A699" },
    cancelled: { label: "Cancelled", bg: "#DC262622", color: "#DC2626" },
  };
  const s = map[status] || map.active;
  return (
    <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const RATING_ATTRS = [
  { key: "punctuality",  label: "Punctuality" },
  { key: "fluency",      label: "Fluency of language" },
  { key: "content",      label: "Content of course taken" },
  { key: "availability", label: "Availability for problem solving" },
];


function StarRow({ label, value, onChange, testId }) {
  return (
    <div className="flex flex-col gap-1 mb-2">
      <span className="text-xs font-semibold" style={{ color: "#13A699" }}>{label}</span>
      <div className="flex items-center gap-1" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className="p-1 transition hover:scale-110"
            aria-label={`Rate ${label} ${n}`}
            data-testid={`${testId}-star-${n}`}
          >
            <Star
              className="h-6 w-6"
              style={{ color: "#13A699" }}
              fill={n <= value ? "#FFD708" : "transparent"}
            />
          </button>
        ))}
      </div>
    </div>
  );
}


export function RatingBlockModal({ enrollment, onRated }) {
  const [rating, setRating] = useState(5);
  // Sub-attribute stars default to 0 (unset) so the parent must consciously pick
  // each score before submitting — client-side check catches the 0s below.
  const [punctuality, setPunctuality] = useState(0);
  const [fluency, setFluency] = useState(0);
  const [content, setContent] = useState(0);
  const [availability, setAvailability] = useState(0);
  const [review, setReview] = useState("");
  const [saving, setSaving] = useState(false);

  if (!enrollment) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1 || rating > 5) return toast.error("Please pick an overall rating");
    for (const [name, v] of [["Punctuality", punctuality], ["Fluency", fluency], ["Content", content], ["Availability", availability]]) {
      if (v < 1 || v > 5) return toast.error(`Please rate ${name}`);
    }
    if (!review.trim()) return toast.error("Please write a review");
    setSaving(true);
    try {
      await api.post("/parent/enrollments/punch-in-and-rate", {
        enrollment_id: enrollment.id,
        rating,
        punctuality,
        fluency,
        content,
        availability,
        review: review.trim(),
      });
      toast.success("Punched in & rated. Thanks!");
      onRated && onRated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4 py-6" data-testid="rating-block-modal">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="h-5 w-5" style={{ color: "#FBA312" }} />
          <h2 className="font-display text-xl font-bold" style={{ color: "#13A699" }}>Punch In &amp; Rate</h2>
        </div>
        <p className="text-sm" style={{ color: "#13A699" }}>
          Your mentor has punched in to mark <strong>{enrollment.course_title}</strong> complete. Please <strong>punch in</strong> to confirm and rate the mentor on each attribute — all are required to mark it completed.
        </p>

        <form onSubmit={submit} className="mt-5">
          <div className="mb-3">
            <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: "#13A699" }}>Overall rating</span>
            <div className="flex items-center gap-1 mt-1" data-testid="rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="p-1 transition hover:scale-110"
                  aria-label={`Rate ${n}`}
                  data-testid={`star-${n}`}
                >
                  <Star
                    className="h-8 w-8"
                    style={{ color: "#13A699" }}
                    fill={n <= rating ? "#FFD708" : "transparent"}
                  />
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-black/10 pt-3">
            <StarRow label={RATING_ATTRS[0].label} value={punctuality}  onChange={setPunctuality}  testId="rate-punctuality" />
            <StarRow label={RATING_ATTRS[1].label} value={fluency}      onChange={setFluency}      testId="rate-fluency" />
            <StarRow label={RATING_ATTRS[2].label} value={content}      onChange={setContent}      testId="rate-content" />
            <StarRow label={RATING_ATTRS[3].label} value={availability} onChange={setAvailability} testId="rate-availability" />
          </div>

          <label className="block text-sm font-semibold mt-3" style={{ color: "#13A699" }}>
            Your review (required)
            <textarea
              rows={4}
              value={review}
              onChange={(e) => setReview(e.target.value)}
              placeholder="Share how the course helped your child…"
              className="mt-1 w-full rounded-xl border border-black/10 px-3 py-2 bg-white text-[#13A699] focus:ring-2 focus:ring-[#13A699] outline-none"
              data-testid="review-text"
            />
          </label>
          <div className="mt-5 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="btn-teal px-5 py-2 text-sm"
              data-testid="submit-rating-btn"
            >
              {saving ? "Submitting…" : "Punch In & Submit Rating"}
            </button>
          </div>
          <p className="mt-3 text-[11px]" style={{ color: "#13A69988" }}>
            Reviews are permanent and visible on the mentor's profile. They cannot be deleted later.
          </p>
        </form>
      </div>
    </div>
  );
}
